import { useStaffStore } from "../../stores/staff";
import { useButtonStore } from "../../stores/components/button";
import { Input } from "../ui/inputs/Input";
import { useEffect, useState } from "react";
import { getDepartments } from "../../api/departments";
import { showToastError, showToastSuccess } from "../../services/toast";
import { Select } from "../ui/inputs/Select";
import WorkingHours from "./WorkingHours";
import { SelectCheckbox } from "../ui/inputs/SelectCheckbox";
import { getRoles } from "../../api/roles";
import {
  createUser,
  getUser,
  sendAccess,
  updateUser,
} from "../../api/users";
import { formatDateForInput } from "../../services/function";
import { ActionButton, DetailHeader } from "../ui/page/PageShell";

const User = () => {
  const {
    formData,
    setFormData,
    idUser,
    viewOrUpdate,
    setViewOrUpdate,
    weekHours,
    setWeekHours,
    resetStaff,
    setIdUser,
  } = useStaffStore();
  const { setCreateButton } = useButtonStore();
  const [departments, setDepartments] = useState<any>([]);
  const [roles, setRoles] = useState<any>([]);

  useEffect(() => {
    if (idUser !== 0) {
      getUser(idUser)
        .then((res) => {
          const data = res.data;

          // Formatage des dates pour affichage dans les inputs
          data.hiringDate = formatDateForInput(data.hiringDate);
          data.leavingDate = formatDateForInput(data.leavingDate);

          const formattedDepartments = data.departments.map(
            (department: any) => ({
              label: department.name,
              value: department.id,
            })
          );

          setFormData({
            ...data,
            optionSelected: formattedDepartments,
          });

          // Création d'un objet de référence avec les horaires existants
          const schedulesMap = new Map(
            data.schedules.map((schedule: any) => [schedule.day, schedule])
          );

          // Fusionne les horaires existants avec ceux par défaut
          const updatedWeekHours = weekHours.map((day) => {
            if (schedulesMap.has(day.day)) {
              const schedule = schedulesMap.get(day.day);
              return schedule ? { ...day, ...schedule } : day;
            }
            return day; // On garde le jour avec les valeurs par défaut s'il n'est pas dans l'API
          });

          setWeekHours(updatedWeekHours);
        })
        .catch(() => showToastError("Une erreur est survenue"));
    }
  }, [idUser]);

  const handleSubmitCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Retire les valeurs inutiles pour la création d'un utilisateur
    const { optionSelected, schedules, ...rest } = formData;
    const data = {
      ...rest,
      idDepartments: optionSelected.map((option: any) => Number(option.value)),
      schedule: weekHours,
    };

    {
      viewOrUpdate === ""
        ? createUser(data)
            .then(() => {
              setCreateButton(false);
              setViewOrUpdate("");
              resetStaff();
              showToastSuccess("L'utilisateur a bien été créé");
            })
            .catch((err) => {
              if (err.response.status === 400) {
                showToastError("L'utilisateur existe déjà");
              } else {
                showToastError("Une erreur est survenue");
              }
            })
        : updateUser(idUser, data)
            .then(() => {
              setCreateButton(false);
              setViewOrUpdate("");
              resetStaff();
              showToastSuccess("L'utilisateur a bien été modifié");
            })
            .catch(() => showToastError("Une erreur est survenue"));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: Number(e.target.value) });
  };
  const handleSelectChangeBox = (selected: any) => {
    setFormData({ ...formData, optionSelected: selected });
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };

  // Récupère les services pour les afficher dans le select
  useEffect(() => {
    getDepartments()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setDepartments(formattedData);
      })
      .catch(() => showToastError("Une erreur est survenue"));

    getRoles()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        const filterWithoutClient = formattedData.filter(
          (role: any) => role.value !== 4
        );
        setRoles(filterWithoutClient);
      })
      .catch(() => showToastError("Une erreur est survenue"));
  }, []);

  const handleSendAccess = (id: number) => {
    sendAccess(id)
      .then(() => {
        showToastSuccess("Accès renvoyé");
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <DetailHeader
        title={`${
          viewOrUpdate === "update" ? "Modification" : "Création"
        } d'un utilisateur`}
        hint="Merci de renseigner les informations du collaborateur"
        onClose={() => {
          setCreateButton(false);
          setFormData({});
          setViewOrUpdate("");
          setIdUser(0);
        }}
        actions={
          <ActionButton variant="primary" type="submit" form="userForm">
            {viewOrUpdate !== "" ? "Modifier" : "Créer"}
          </ActionButton>
        }
      />

      <div className="flex min-h-0 w-full flex-1 items-start overflow-hidden p-3 sm:p-4">
        <form
          className="flex h-full min-h-0 w-full flex-col gap-3 overflow-hidden lg:flex-row"
          onSubmit={handleSubmitCreate}
          id="userForm"
        >
          <div className="h-full min-h-0 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 lg:w-1/3">
            <h4 className="mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Informations générales
            </h4>
            <Input
              label="Prénom"
              name="firstName"
              value={formData.firstName || ""}
              required
              onChange={handleChange}
              clasName="w-full 2xl:mt-5 mt-2"
            />
            <Input
              label="Nom"
              name="lastName"
              value={formData.lastName || ""}
              required
              onChange={handleChange}
              clasName="w-full 2xl:mt-5 mt-2"
            />
            <Input
              label="Email"
              name="email"
              required
              value={formData.email || ""}
              onChange={handleChange}
              clasName="w-full 2xl:mt-5 mt-2"
            />
            <Select
              label="Rôle"
              name="idRole"
              value={formData.idRole || ""}
              onChange={handleSelectChange}
              fetchOptions={roles}
              className="w-full 2xl:mt-5 mt-2"
            />
            <SelectCheckbox
              label="Départements"
              name="departments"
              value={formData.optionSelected}
              onChange={handleSelectChangeBox}
              fetchOptions={departments}
              className="w-full 2xl:mt-5 mt-2"
            />
            <div className="flex justify-between items-center gap-4">
                <Input
                label="Taux horaire"
                name="hourlyRate"
                type="number"
                value={formData.hourlyRate || ""}
                onChange={handleChangeNumber}
                clasName="w-1/2 2xl:mt-5 mt-2"
                />
              <Input
                label="Matricule"
                name="matricule"
                value={formData.matricule || ""}
                required
                onChange={handleChange}
                clasName="w-1/2 2xl:mt-5 mt-2"
                />
              </div>
            {viewOrUpdate !== "" && (
              <div className="mt-4">
                <ActionButton
                  onClick={(e) => {
                    e.preventDefault();
                    handleSendAccess(Number(idUser));
                  }}
                >
                  Renvoyer l'accès
                </ActionButton>
              </div>
            )}
          </div>

          <div className="h-full min-h-0 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 lg:w-2/3">
            <h4 className="mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Horaires
            </h4>
            <div className="w-full flex justify-start items-center gap-4">
              <Input
                label="Date d'entrée"
                name="hiringDate"
                required
                type="date"
                value={formData.hiringDate || ""}
                onChange={handleChange}
                clasName="w-44 2xl:mt-5 mt-2"
              />
              <Input
                label="Date de fin"
                name="leavingDate"
                type="date"
                value={formData.leavingDate || ""}
                onChange={handleChange}
                clasName="w-44 2xl:mt-5 mt-2"
              />
            </div>

            {/* Affichage des heures de travail */}
            <WorkingHours />
          </div>
        </form>
      </div>
    </div>
  );
};

export default User;
